// CONVERSATION-TYPE THEMING — the single source of truth for how a conversation window is skinned.
// Three types, three complete treatments. The skin is derived from the OTHER PARTY, never from a
// single tag/border (too easy to miss). Colours carry to the member side; LABELS change by audience.
//
// RESERVED COLOURS — each used for NOTHING else in the product:
//   olive   -> INTERNAL · STAFF
//   #5E2FA8 -> EXTERNAL · MEMBER
//   #1F6BA8 -> EXTERNAL · BUSINESS
export const CONV_INTERNAL = "internal";
export const CONV_MEMBER = "member";
export const CONV_BUSINESS = "business";

export const CONV_THEME = {
  // INTERNAL · STAFF — olive. Staff-facing only; members never see this skin (support renders it,
  // labelled OpticSwap Support, because from a member's side we ARE the platform).
  internal: {
    key: "internal",
    windowBg: "#141619", windowBorder: "#2A2E24", rgb: "87,104,42",
    headerBg: "#25272B", headerText: "#EDF2DE",
    pill: { bg: "#57682A", border: "#657A32", text: "#EDF2DE" },
    subheaderBg: "#141619", subheaderText: "#76797D", subheaderBorder: "#25272B",
    avatarBg: "#242A18", avatarBorder: "#57682A", avatarInitials: "#AEC07B",
    senderName: "#C7D3A6", body: "#D6D8DC",
    composerBg: "#141619", composerBorder: "#25272B",
    inputBg: "#0F1113", inputBorder: "#2A2E24", placeholder: "#76797D",
    sendBg: "#57682A", sendBorder: "#657A32", sendText: "#EDF2DE",
    bar: "#57682A",
  },
  // EXTERNAL · MEMBER — deep purple #5E2FA8.
  member: {
    key: "member",
    windowBg: "#191323", windowBorder: "#4A3866", rgb: "94,47,168",
    headerBg: "#5E2FA8", headerText: "#EBE0F7",
    pill: { bg: "#3D1D70", border: "#8C6BB8", text: "#E4D4F7" },
    subheaderBg: "#150F1F", subheaderText: "#B9A8CE", subheaderBorder: "#3A2B52",
    avatarBg: "#241735", avatarBorder: "#5E2FA8", avatarInitials: "#AE8FD9",
    senderName: "#DACAEE", body: "#D6D8DC",
    composerBg: "#150F1F", composerBorder: "#3A2B52",
    inputBg: "#100B18", inputBorder: "#3A2B52", placeholder: "#8E7FA6",
    sendBg: "#5E2FA8", sendBorder: "#7B4FBF", sendText: "#EFE6FA",
    bar: "#5E2FA8",
  },
  // EXTERNAL · BUSINESS — blue #1F6BA8.
  business: {
    key: "business",
    windowBg: "#0F1A24", windowBorder: "#2A4C68", rgb: "31,107,168",
    headerBg: "#1F6BA8", headerText: "#DDEBF6",
    pill: { bg: "#123A57", border: "#4A83AC", text: "#CFE4F3" },
    subheaderBg: "#0C1620", subheaderText: "#9FBDD4", subheaderBorder: "#22405A",
    avatarBg: "#12283A", avatarBorder: "#1F6BA8", avatarInitials: "#8FBEDC",
    senderName: "#C2DCEE", body: "#D6D8DC",
    composerBg: "#0C1620", composerBorder: "#22405A",
    inputBg: "#091119", inputBorder: "#22405A", placeholder: "#7C97AC",
    sendBg: "#1F6BA8", sendBorder: "#3E7FAC", sendText: "#E4F1FA",
    bar: "#1F6BA8",
  },
};

export function convTheme(type) { return CONV_THEME[type] || CONV_THEME.member; }

// Derive the skin from the OTHER PARTY. Staff/support -> internal. Business/dealer -> business.
// Everyone else (or unknown external sender) -> individual member.
export function resolveConvType(party) {
  if (!party) return CONV_MEMBER;
  if (party.is_support || party.is_staff || party.kind === "staff" ||
      party.role === "admin" || party.role === "super_admin") return CONV_INTERNAL;
  if (party.party_type === "business" || party.is_business || party.is_dealer ||
      party.account_type === "business") return CONV_BUSINESS;
  if (party.party_type === "internal") return CONV_INTERNAL;
  return CONV_MEMBER;
}

// Labels change by AUDIENCE. "EXTERNAL · MEMBER" is meaningless to a member — from where they sit
// everyone is external — so members get audience-appropriate labels on the SAME colours.
//   admin:  Internal·Staff | External·Member | External·Business
//   member: (n/a)          | Individual Seller/Buyer | Business · Dealer   ; support -> OpticSwap Support
export function convLabels(type, { audience = "admin", context = "seller" } = {}) {
  if (type === CONV_INTERNAL) {
    return audience === "member"
      ? { pill: "OpticSwap Support", sub: null }
      : { pill: "Internal · Staff", sub: "Admin · not visible to members" };
  }
  if (type === CONV_MEMBER) {
    return audience === "member"
      ? { pill: context === "buyer" ? "Individual Buyer" : "Individual Seller", sub: null }
      : { pill: "External · Member", sub: "Individual · they see everything here" };
  }
  return audience === "member"
    ? { pill: "Business · Dealer", sub: null }
    : { pill: "External · Business", sub: "Business · they see everything here" };
}

// Sidebar group headings (colour + label). Never colour alone — always paired with the heading
// text and a per-row type pill.
export const SIDEBAR_GROUPS = {
  internal: { heading: "// Internal · staff", color: "#C4A63D", short: "Staff" },
  member: { heading: "// External · members", color: "#B9A8CE", short: "Member" },
  business: { heading: "// External · businesses", color: "#8FBEDC", short: "Business" },
};
